import React, { useState } from 'react';
import { ChevronDown, Download } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { LoadingState, StatCard, fmt } from './ReportHelpers';

export function PnLTab({ data, loading, onExport }) {
  const [expandedClient, setExpandedClient] = useState(null);

  if (loading) return <LoadingState />;
  if (!data) return <div className="text-center py-8 text-slate-400">No data available</div>;

  const { totals, clients: clientsList } = data;

  const handleExport = () => {
    const headers = ['Client', 'Jobs', 'Revenue', 'Cost', 'Profit', 'Margin %'];
    const rows = clientsList.map(c => [c.client_name, c.job_count, fmt(c.revenue), fmt(c.cost), fmt(c.profit), `${c.margin}%`]);
    const summaryRow = ['TOTAL', totals.job_count, fmt(totals.revenue), fmt(totals.cost), fmt(totals.profit), `${totals.margin}%`];
    onExport('Profit & Loss Report', headers, rows, summaryRow);
  };

  const chartData = clientsList.slice(0, 10).map(c => ({
    name: c.client_name.length > 18 ? c.client_name.slice(0, 18) + '...' : c.client_name,
    Revenue: c.revenue,
    Cost: c.cost,
    Profit: c.profit,
  }));

  return (
    <div className="space-y-6" data-testid="pnl-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Revenue" value={fmt(totals.revenue)} color="bg-[#178c98]" />
        <StatCard label="Total Cost" value={fmt(totals.cost)} color="bg-[#e53e3e]" />
        <StatCard label="Gross Profit" value={fmt(totals.profit)} color="bg-[#38a169]" />
        <StatCard label="Overall Margin" value={`${totals.margin}%`} sub={`${totals.job_count} jobs`} color="bg-[#f4931d]" />
      </div>

      {chartData.length > 0 && (
        <div className="bg-white rounded-[10px] border p-4">
          <h3 className="font-semibold text-slate-700 mb-4">Revenue vs Cost by Client</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend />
              <Bar dataKey="Revenue" fill="#178c98" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Cost" fill="#e53e3e" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Profit" fill="#38a169" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white rounded-[10px] border overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-semibold text-slate-700">P&L by Client</h3>
          <button onClick={handleExport} className="flex items-center gap-1 text-xs px-3 py-1.5 bg-[#f4931d] text-white rounded hover:bg-[#d67e12]" data-testid="export-pnl">
            <Download size={13} /> Export
          </button>
        </div>
        <table className="w-full" data-testid="pnl-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 w-8"></th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Client</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">Jobs</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Revenue</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Cost</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Profit</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Margin</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {clientsList.map((c) => (
              <React.Fragment key={c.client_id}>
                <tr
                  className="hover:bg-orange-50/30 cursor-pointer transition-colors"
                  onClick={() => setExpandedClient(expandedClient === c.client_id ? null : c.client_id)}
                  data-testid={`pnl-client-${c.client_id}`}
                >
                  <td className="px-4 py-3">
                    <ChevronDown size={14} className={`transition-transform ${expandedClient === c.client_id ? 'rotate-180' : ''}`} />
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">{c.client_name}</td>
                  <td className="px-4 py-3 text-sm text-center">{c.job_count}</td>
                  <td className="px-4 py-3 text-sm text-right">{fmt(c.revenue)}</td>
                  <td className="px-4 py-3 text-sm text-right text-red-600">{fmt(c.cost)}</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold" style={{ color: c.profit >= 0 ? '#38a169' : '#e53e3e' }}>{fmt(c.profit)}</td>
                  <td className="px-4 py-3 text-sm text-right">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.margin >= 20 ? 'bg-green-100 text-green-700' : c.margin >= 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                      {c.margin}%
                    </span>
                  </td>
                </tr>
                {expandedClient === c.client_id && c.jobs.map((j) => (
                  <tr key={j.job_id} className="bg-slate-50/50">
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2 text-xs text-slate-500 pl-8">{j.job_number}</td>
                    <td className="px-4 py-2 text-xs text-center text-slate-500">
                      {j.status}
                    </td>
                    <td className="px-4 py-2 text-xs text-right text-slate-600">{fmt(j.revenue)}</td>
                    <td className="px-4 py-2 text-xs text-right text-red-500">{fmt(j.cost)}</td>
                    <td className="px-4 py-2 text-xs text-right font-medium" style={{ color: j.profit >= 0 ? '#38a169' : '#e53e3e' }}>{fmt(j.profit)}</td>
                    <td className="px-4 py-2 text-xs text-right text-slate-500">{j.margin}%</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
          {clientsList.length > 0 && (
            <tfoot>
              <tr className="bg-slate-800 text-white font-semibold">
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3">TOTAL</td>
                <td className="px-4 py-3 text-center">{totals.job_count}</td>
                <td className="px-4 py-3 text-right">{fmt(totals.revenue)}</td>
                <td className="px-4 py-3 text-right">{fmt(totals.cost)}</td>
                <td className="px-4 py-3 text-right">{fmt(totals.profit)}</td>
                <td className="px-4 py-3 text-right">{totals.margin}%</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
